import {
    Badge,
    Text,
} from '@mantine/core';
import {I18n} from 'aws-amplify/utils';
import {PlatformPropertyCard} from '@flexinet/ui-components';

import {
    delegationState,
    DnsZone,
    recordCountState,
} from '../types';
import {providerLabel} from '../providerLabels';

// The delegation set the provider publishes for this zone. `nameservers` is
// `[String!]!` — non-null — so an empty list is a real answer meaning the
// provider published none, not a field that failed to load.
const NameServers = ({nameservers}: { nameservers?: string[] | null }) => {

    if (!nameservers || nameservers.length === 0) {
        return (
            <Text size={'sm'} c={'dimmed'} fs={'italic'}>
                {I18n.get('This provider published no nameservers for the zone')}
            </Text>
        );
    }

    return (
        <>
            {nameservers.map(ns => (
                <Text key={ns} size={'xs'} ff={'monospace'}>{ns}</Text>
            ))}
        </>
    );
};

// FNP-1422 — the record count, with the absence spelled out.
//
// ⚠️ THE TABLE GETS A DASH; HERE THERE IS ROOM TO SAY WHY. Same reasoning as
// Delegation below: the UNDETERMINED case is the one that needs words, because
// a dash alone reads as "none" to anyone not primed to see it as "unknown".
// And the wrong reading is consequential — a zone believed empty is a zone
// someone may delete.
const RecordCountDetail = ({zone}: { zone: DnsZone }) => {

    const state = recordCountState(zone);

    if (state.kind === 'NOT_REPORTED') {
        return (
            <>
                <Text size={'sm'} c={'dimmed'}>{I18n.get('Not reported')}</Text>
                <Text size={'xs'} c={'dimmed'} mt={4}>
                    {I18n.get('This provider did not report a record count. This is not the same as the zone being empty — no count was given either way.')}
                </Text>
            </>
        );
    }

    // ⚠️ A REAL ZERO SAYS SO, IN WORDS. Rendering a bare "0" beside the
    // "Not reported" case above would put the two states one character apart.
    if (state.count === 0) {
        return (
            <>
                <Text size={'sm'}>{I18n.get('0 records')}</Text>
                <Text size={'xs'} c={'dimmed'} mt={4}>
                    {I18n.get('The provider reports this zone holds no records.')}
                </Text>
            </>
        );
    }

    return <Text size={'sm'}>{state.count}</Text>;
};

// ⚠️ THE TRI-STATE, SPELLED OUT IN FULL ON THE DETAIL PAGE.
//
// The table gets a badge; here there is room to say what the state actually
// means, and the UNDETERMINED case is where that matters. "Appears" is
// load-bearing in the schema: this is a read of the provider's own published
// nameservers, not a live resolver query against the registrar, so it can be
// confidently wrong in the minutes after a delegation change.
const Delegation = ({zone}: { zone: DnsZone }) => {

    const state = delegationState(zone);

    if (state === 'DELEGATED') {
        return (
            <>
                <Badge variant={'light'} color={'green'}>{I18n.get('Appears delegated')}</Badge>
                <Text size={'xs'} c={'dimmed'} mt={4}>
                    {I18n.get('The registrar nameservers appear to match what this provider publishes.')}
                </Text>
            </>
        );
    }

    if (state === 'NOT_DELEGATED') {
        return (
            <>
                <Badge variant={'light'} color={'red'}>{I18n.get('Does not appear delegated')}</Badge>
                <Text size={'xs'} c={'dimmed'} mt={4}>
                    {I18n.get('The registrar nameservers do not appear to match what this provider publishes. If you changed the delegation recently, this may catch up shortly.')}
                </Text>
            </>
        );
    }

    // UNDETERMINED — null or absent. NOT rendered as "not delegated".
    return (
        <>
            <Badge variant={'outline'} color={'gray'}>{I18n.get('Not determined')}</Badge>
            <Text size={'xs'} c={'dimmed'} mt={4}>
                {I18n.get('Delegation was not determined for this zone. This is not the same as "not delegated" — no conclusion was reached either way.')}
            </Text>
        </>
    );
};

export const Profile = ({record}: { record: DnsZone }) => {

    const properties = [
        {
            label: 'Name',
            value: record.name,
            span: 12,
        },
        {
            label: 'Provider',
            value: (
                <Badge variant={'light'}>
                    {providerLabel(record.provider)}
                </Badge>
            ),
            span: 12,
        },
        {
            label: 'Visibility',
            value: (
                <Badge variant={'light'} color={record.isPrivate ? 'orange' : 'blue'}>
                    {record.isPrivate ? I18n.get('Private') : I18n.get('Public')}
                </Badge>
            ),
            span: 12,
        },
        {
            label: 'Delegation',
            value: <Delegation zone={record}/>,
            span: 12,
        },
        {
            label: 'Name Servers',
            value: <NameServers nameservers={record.nameservers}/>,
            span: 12,
        },
        {
            label: 'Records',
            value: <RecordCountDetail zone={record}/>,
            span: 12,
        },
        {
            label: 'Connection',
            // The connection is shown because it is what makes the zone
            // ADDRESSABLE — the same zone name under two Route 53 connections
            // is two different zones.
            value: <Text size={'xs'} ff={'monospace'}>{record.connectionId}</Text>,
            span: 12,
        },
        // ⚠️ FLEXI'S OWN ZONE ID IS STILL DELIBERATELY NOT SHOWN.
        //
        // It is OPAQUE — minted by the adapter, meaningful to nothing outside
        // this platform. Displaying it invites an operator to paste it into the
        // AWS or Cloudflare console, where it means nothing.
        //
        // ⚠️ THE PROVIDER'S OWN ID BELOW IS THE ANSWER TO THAT PROBLEM, NOT AN
        // EXCEPTION TO IT (FNP-1425). The reason the Flexi id is hidden is that
        // an operator who needs to reach the provider's console cannot use it.
        // Hiding both left them with nothing, and the hosted-zones module — the
        // one place the true Route 53 id was visible — is being retired. So the
        // usable id is shown, LABELLED as the provider's, while the opaque one
        // stays hidden. Nothing here parses it.
        ...(record.providerZoneId
            ? [{
                label: `${providerLabel(record.provider)} zone ID`,
                value: (
                    <Text size={'xs'} ff={'monospace'}>{record.providerZoneId}</Text>
                ),
                span: 12,
            }]
            : []),
    ];

    return (
        <PlatformPropertyCard properties={properties}/>
    );
};
